// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * render.ts — the well, drawn on a canvas that actually hangs.
 *
 * The whole board is rendered inside a rotated frame whose angle is the list.
 * That single visual is what teaches the mechanic in the first second: you pile
 * up on one side, the well leans, and you can SEE why the next piece slides.
 * Particles are emitted inside that frame too, so debris falls along the
 * list rather than straight down.
 *
 * Cell size is solved against the ROTATED bounding box, not the upright one —
 * otherwise a leaning well clips its own corners off the screen at 375px.
 */

import { EMPTY, cellsAt } from './pieces';
import { MARKS, PIECE_COLORS, SURFACE } from './palette';
import { TRIM_BAND, type Game } from './game';

/**
 * Degrees of lean at |list| = 1.
 *
 * This was 3.6° mapped LINEARLY, and that made the game's whole feedback channel
 * sub-perceptual: a normal player's well averages |list| ≈ 0.12, so it leaned
 * 0.45° — invisible. Even a player deliberately piling to one side only reached
 * ~2.4°. The mechanic was firing constantly and could not be seen, which is
 * exactly what "it's a cool mechanic but it never gets triggered" looks like
 * from the player's seat.
 *
 * Real play occupies |list| 0.1–0.65, not 0–1, so the curve is shaped to that
 * band rather than to the theoretical maximum. See `tiltFor`.
 */
export const MAX_TILT_DEG = 8;
export const MAX_TILT_DEG_REDUCED = 1.2;
/**
 * Exponent on |list|. Below 1 it lifts the small, common values into view.
 *
 * Pinned at 0.6 by the mode with the LOWEST drift threshold: Broadside fires at
 * |list| 0.14 (a measured balance result, not a dial), and the lean has to be
 * clearly visible at the moment the mechanic starts acting on the player — in
 * every mode, not just the ones that lean furthest before it bites.
 */
const TILT_GAMMA = 0.6;

/**
 * Rendered lean for a given list. Deliberately non-linear:
 *   in trim (|l| < 0.15) → under ~2.3°, present but calm
 *   at the drift threshold (0.22) → ~3°, clearly leaning
 *   a heavy list (0.5+) → 5–8°, alarming
 * Cell size is solved against this angle, so the well still never clips.
 */
export function tiltFor(list: number, reduced: boolean): number {
  const max = ((reduced ? MAX_TILT_DEG_REDUCED : MAX_TILT_DEG) * Math.PI) / 180;
  const mag = Math.min(1, Math.abs(list)) ** TILT_GAMMA;
  return Math.sign(list) * mag * max;
}

interface Particle {
  x: number; y: number; vx: number; vy: number; life: number; max: number; color: string; size: number;
}

export interface DrawOpts {
  /** 0..1 progress of the row-clear flash. */
  flashRows?: number[];
  flash?: number;
  paused?: boolean;
}

export interface Renderer {
  resize(): void;
  draw(game: Game, dtMs: number, opts?: DrawOpts): void;
  burst(game: Game, cells: [number, number][], color?: string): void;
  shake(amount: number): void;
  setReducedMotion(v: boolean): void;
  /** Board geometry in CSS pixels — the touch layer needs the cell size. */
  metrics(): { cell: number; boardW: number; boardH: number };
}

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');

  let cssW = 0;
  let cssH = 0;
  let cell = 24;
  let boardW = 0;
  let boardH = 0;
  let shakeAmt = 0;
  let tilt = 0;
  let reduced = false;
  const particles: Particle[] = [];

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    // A transient 0-size measurement yields NaN world coords and silently eats
    // every tap, so ignore it and let the next frame retry.
    if (rect.width < 2 || rect.height < 2) return;
    cssW = rect.width;
    cssH = rect.height;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function fitCell(cols: number, rows: number): void {
    const th = ((reduced ? MAX_TILT_DEG_REDUCED : MAX_TILT_DEG) * Math.PI) / 180;
    const c = Math.cos(th);
    const s = Math.sin(th);
    const pad = 10;
    const byW = (cssW - pad * 2) / (cols * c + rows * s);
    const byH = (cssH - pad * 2 - 14) / (cols * s + rows * c);
    cell = Math.max(6, Math.floor(Math.min(byW, byH)));
    boardW = cell * cols;
    boardH = cell * rows;
  }

  function roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const rr = Math.min(r, w / 2, h / 2);
    ctx!.beginPath();
    ctx!.moveTo(x + rr, y);
    ctx!.arcTo(x + w, y, x + w, y + h, rr);
    ctx!.arcTo(x + w, y + h, x, y + h, rr);
    ctx!.arcTo(x, y + h, x, y, rr);
    ctx!.arcTo(x, y, x + w, y, rr);
    ctx!.closePath();
  }

  function drawCell(c: number, r: number, color: string, alpha = 1): void {
    const g = ctx!;
    const x = c * cell;
    const y = r * cell;
    const inset = Math.max(1, cell * 0.06);
    g.globalAlpha = alpha;
    g.fillStyle = color;
    roundRect(x + inset, y + inset, cell - inset * 2, cell - inset * 2, cell * 0.18);
    g.fill();
    // A light top edge gives each block weight without a second palette entry.
    g.globalAlpha = alpha * 0.28;
    g.fillStyle = '#ffffff';
    roundRect(x + inset, y + inset, cell - inset * 2, Math.max(1.5, cell * 0.16), cell * 0.14);
    g.fill();
    g.globalAlpha = 1;
  }

  function draw(game: Game, dtMs: number, opts: DrawOpts = {}): void {
    if (cssW < 2 || cssH < 2) { resize(); if (cssW < 2) return; }
    fitCell(game.cols, game.rows);
    const g = ctx!;

    const target = tiltFor(game.list(), reduced);
    // Ease toward the target so the well swings rather than snapping.
    tilt += (target - tilt) * Math.min(1, dtMs / 90);

    g.clearRect(0, 0, cssW, cssH);

    let sx = 0;
    let sy = 0;
    if (shakeAmt > 0.05 && !reduced) {
      sx = (Math.random() - 0.5) * shakeAmt;
      sy = (Math.random() - 0.5) * shakeAmt;
      shakeAmt *= Math.pow(0.0025, dtMs / 1000);
    } else shakeAmt = 0;

    const cx = cssW / 2;
    const cy = (cssH + 12) / 2;

    // The pivot the well hangs from — drawn in screen space so it stays put
    // while the well swings under it.
    g.strokeStyle = 'rgba(142,164,184,0.5)';
    g.lineWidth = 1.5;
    g.beginPath();
    g.arc(cx, 7, 3.5, 0, Math.PI * 2);
    g.stroke();

    g.save();
    g.translate(cx + sx, cy + sy);
    g.rotate(tilt);
    g.translate(-boardW / 2, -boardH / 2);

    // The two hanging lines, in board space, so they lean with it.
    g.strokeStyle = 'rgba(142,164,184,0.32)';
    g.beginPath();
    g.moveTo(0, 0);
    g.lineTo(boardW / 2, -(cy - 7));
    g.lineTo(boardW, 0);
    g.stroke();

    // Well floor + walls.
    g.fillStyle = SURFACE.well;
    roundRect(0, 0, boardW, boardH, 6);
    g.fill();

    g.strokeStyle = 'rgba(255,255,255,0.045)';
    g.lineWidth = 1;
    g.beginPath();
    for (let c = 1; c < game.cols; c++) { g.moveTo(c * cell, 0); g.lineTo(c * cell, boardH); }
    for (let r = 1; r < game.rows; r++) { g.moveTo(0, r * cell); g.lineTo(boardW, r * cell); }
    g.stroke();

    // Settled cells.
    for (let r = 0; r < game.rows; r++) {
      for (let c = 0; c < game.cols; c++) {
        const v = game.cells[game.idx(c, r)];
        if (v === EMPTY) continue;
        drawCell(c, r, PIECE_COLORS[v] ?? MARKS.pop);
      }
    }

    // The row-clear flash.
    if (opts.flashRows?.length && (opts.flash ?? 0) > 0) {
      g.globalAlpha = Math.min(1, opts.flash ?? 0) * 0.85;
      g.fillStyle = '#ffffff';
      for (const r of opts.flashRows) g.fillRect(0, r * cell, boardW, cell);
      g.globalAlpha = 1;
    }

    // Ghost, then the live piece.
    const gh = game.ghost();
    if (gh && !game.over) {
      g.strokeStyle = MARKS.ghost;
      g.lineWidth = Math.max(1.5, cell * 0.08);
      g.globalAlpha = 0.75;
      for (const [c, r] of cellsAt(gh.type, gh.rot, gh.x, gh.y)) {
        const inset = Math.max(2, cell * 0.14);
        roundRect(c * cell + inset, r * cell + inset, cell - inset * 2, cell - inset * 2, cell * 0.14);
        g.stroke();
      }
      g.globalAlpha = 1;
    }
    if (game.cur && !game.over) {
      const col = PIECE_COLORS[game.cur.type];
      for (const [c, r] of cellsAt(game.cur.type, game.cur.rot, game.cur.x, game.cur.y)) {
        if (r >= 0) drawCell(c, r, col);
      }
    }

    // Particles live in board space so debris falls along the list.
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life -= dtMs;
      if (p.life <= 0) { particles.splice(i, 1); continue; }
      p.vy += (dtMs / 1000) * 900;
      p.x += (p.vx * dtMs) / 1000;
      p.y += (p.vy * dtMs) / 1000;
      g.globalAlpha = Math.max(0, p.life / p.max);
      g.fillStyle = p.color;
      g.fillRect(p.x, p.y, p.size, p.size);
    }
    g.globalAlpha = 1;

    // Rim: green in trim, warm when the stack is near the ceiling.
    const height = game.stackHeight();
    const danger = height >= game.rows - 4;
    const inTrim = Math.abs(game.list()) < TRIM_BAND;
    g.lineWidth = 2;
    g.strokeStyle = danger ? MARKS.danger : (inTrim ? MARKS.trim : 'rgba(142,164,184,0.35)');
    g.globalAlpha = danger ? 0.55 + 0.45 * Math.abs(Math.sin(Date.now() / 260)) : 1;
    roundRect(0, 0, boardW, boardH, 6);
    g.stroke();
    g.globalAlpha = 1;

    g.restore();
  }

  return {
    resize,
    draw,
    burst(game, cells, color) {
      if (reduced) return;
      const budget = Math.min(cells.length, 90);
      for (let i = 0; i < budget; i++) {
        const [c, r] = cells[i];
        const v = game.cells[game.idx(c, r)];
        const col = color ?? PIECE_COLORS[v] ?? MARKS.pop;
        for (let k = 0; k < 3; k++) {
          particles.push({
            x: c * cell + cell / 2,
            y: r * cell + cell / 2,
            vx: (Math.random() - 0.5) * 260,
            vy: -Math.random() * 240 - 40,
            life: 380 + Math.random() * 320,
            max: 700,
            color: col,
            size: Math.max(2, cell * 0.18),
          });
        }
      }
      if (particles.length > 400) particles.splice(0, particles.length - 400);
    },
    shake(amount) { if (!reduced) shakeAmt = Math.min(26, shakeAmt + amount); },
    setReducedMotion(v) { reduced = v; },
    metrics: () => ({ cell, boardW, boardH }),
  };
}
